import React, { type PropsWithChildren, useEffect, useState } from 'react';

import { IntlProvider, useIntl } from 'react-intl';

import lazyMessages from '../../i18n/lazy';

/**
 * Code path previously used optional chaining (process?.env?.NODE_ENV) which crashed the townsquare local dev loop
 * This approach removes the chaining, and will fallback if there's any other issues.
 */
function isDev() {
	try {
		return process.env.NODE_ENV !== 'production';
	} catch {
		return false;
	}
}

export const AccountMenuIntlProvider = ({ children }: PropsWithChildren<{}>): React.JSX.Element => {
	const { locale } = useIntl();
	const messages = useMessages(locale);

	return (
		<IntlProvider
			locale={locale}
			messages={messages}
			onError={(err) => {
				// Do not spam local devloop with missing translation errors
				//
				// See: https://github.com/formatjs/formatjs/issues/465#issuecomment-643887472
				// Note the default error handler: https://github.com/formatjs/formatjs/blob/d0b80fd9a814d1427b73577aebb7ccdb80b9ae94/packages/intl/src/utils.ts#L43
				if (isDev() && err?.code !== 'MISSING_TRANSLATION') {
					// eslint-disable-next-line no-console
					console.error(err);
				}
			}}
		>
			{children}
		</IntlProvider>
	);
};

const useMessages = (locale: string) => {
	const [messages, setMessages] = useState<Record<string, string>>();

	useEffect(() => {
		if (/^en(-US)?$/i.exec(locale)) {
			// Use fallback messages, which are already US English
			setMessages(undefined);
		} else {
			let cancelled = false;

			loadMessages(locale)
				.catch((_error) => undefined) // TODO: Log error somehow (analytics?)
				.then((messages) => {
					if (!cancelled) {
						setMessages(messages);
					}
				});

			return () => {
				cancelled = true;
			};
		}
	}, [locale, setMessages]);

	return messages;
};

const loadMessages = async (locale: string): Promise<Record<string, string> | undefined> => {
	let [language, region = ''] = locale.split('-', 2);
	language = language.toLowerCase();
	region = region.toUpperCase();

	return (await (lazyMessages[`${language}_${region}`] || lazyMessages[language])?.())?.default;
};
